import { listTaskTemplates, type TaskTemplateListType } from '@repo/crm-atoms';
import { useGetGraphqlDataQuery, type UseQueryResult } from '@repo/services';
import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, Divider, Flex, Row, Typography,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { nodeSchemas, type TaskCreateNodeData, type TaskCreateNodeType } from 'automations/types/nodeTypes';
import { saveTriggerNode } from 'automations/utils/triggerNode';
import { useCallback, useEffect, useState } from 'react';
import { ZodError } from 'zod';

import AutomationsCriteriaConditionsWrapper from '../AutomationsCriteriaConditionsWrapper';
import { type BuilderUiProps } from '../AutomationsCriteriaConditionsWrapper/AutomationsCriteriaConditionsWrapper';
import ShowConditionsUi from '../ShowConditionsUi';
import styles from './TaskBaseCreateMenu.module.scss';
import TaskTemplateSelection from './TaskTemplateSelection';

type Criteria = TaskCreateNodeData['additionalOptions']['newTask']['criterias'][number];

export default function TaskBaseCreateMenu({
  node,
}: {
  node: NodeProps<TaskCreateNodeType>;
}) {
  const automationId = useAutomationId();
  const dispatch = useAutomationDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);

  const { data: taskTemplateData } = useGetGraphqlDataQuery<UseQueryResult<TaskTemplateListType>>({
    document: listTaskTemplates,
  });

  const [
    selectedTaskTemplate, setSelectedTaskTemplate,
  ] = useState<Criteria[]>(
    node.data?.additionalOptions?.newTask.criterias ?? [],
  );

  const [removalConditionsId, setRemovalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.removableCriteriaGroupId ?? undefined,
  );

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    try {
      const parsedNodeData = nodeSchemas.TaskCreateNodeData.parse({
        ...node.data,
        additionalOptions: {
          newTask: {
            comparator: 'or',
            criterias: selectedTaskTemplate,
          },
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));

      await dispatch(saveTriggerNode(node.id, automationId, contentMode));

      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(
          `Failed to update #{${TranslationEntity.automation}}`,
          [{ capitalize: true }],
        );
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [automationId, dispatch, node.data, node.id, setMenuOpen, selectedTaskTemplate, contentMode]);

  const updateExistingField = useCallback((
    field: Criteria,
    index: number,
  ) => {
    setSelectedTaskTemplate((prev) => prev.map((item, i) => (i === index ? field : item)));
  }, []);

  const deleteField = useCallback((index: number) => {
    setSelectedTaskTemplate((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addNewDefaultField = useCallback(() => {
    if (taskTemplateData?.taskTemplates.length) {
      setSelectedTaskTemplate((prev) => [...prev, {
        taskTemplateId: parseInt(taskTemplateData.taskTemplates[0]?.id ?? '-1', 10),
      }]);
    }
  }, [taskTemplateData?.taskTemplates]);

  useEffect(() => {
    if (taskTemplateData?.taskTemplates.length && selectedTaskTemplate.length === 0) {
      addNewDefaultField();
    }
  }, [taskTemplateData?.taskTemplates, addNewDefaultField, selectedTaskTemplate.length]);

  const ConditionsBuilderUi = useCallback((props: BuilderUiProps) => (
    <ShowConditionsUi
      {...props}
      title="Set Conditions"
      btnClassName={styles.addBtn}
    />
  ), []);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `#{${TranslationEntity.automation}} starts when #{${TranslationEntity.task}} is created`,
          [{ capitalize: true }, { capitalize: true, withArticle: true }],
        ),
      }}
    >
      <Card title={contentMode === 'edit' ? 'Edit Trigger' : 'Set Trigger'} className={styles.containerCard}>
        <TaskTemplateSelection
          selectedTaskTemplates={selectedTaskTemplate}
          taskTemplatesData={taskTemplateData}
          updateExistingField={updateExistingField}
          deleteField={deleteField}
          addNewDefaultField={addNewDefaultField}
        />
        <Divider dashed />
        <Row align="middle">
          <Col span={6}>
            <Typography.Text strong>Set Additional Conditions</Typography.Text>
          </Col>
          <Col span={18}>
            <AutomationsCriteriaConditionsWrapper
              criteriaId={removalConditionsId}
              onCriteriaChange={setRemovalConditionsId}
              builderUi={ConditionsBuilderUi}
            />
          </Col>
        </Row>
      </Card>

      <Flex justify="flex-end" gap={16}>
        <Button onClick={() => setMenuOpen(false)}>Cancel</Button>
        <Button type="primary" onClick={handleSave} loading={isLoading} disabled={isLoading}>Set Trigger</Button>
      </Flex>
    </NodeMenu>
  );
}
